import React, { useMemo, useState } from 'react';
import { Alert, Button, Modal, Table, TableColumnsType } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';
import { DataType } from '../../pages/task_list/data_type';
import { ConflictStatus, SectionDisplayKeys, TimeSlot, TimeSlotList } from '../../../constant';
import { SectionIcon } from '../section_icon/section_icon';
import { Messages } from '../../../constant/messages';
import { useWorkDetails } from '../task_detail/use_work_details';
import { useTaskListStore } from '../../pages/task_list/tasklist_store';
import { WorkListReponse } from '../../../model/work_response';
import { ScheduleStatus } from '../schedule/schedule';
import { ConflictListPage } from '../conflict_list/index';
import { ApiError } from '../../../api/api';
import { useIndicator } from '../../../hook/use_indicator';

/**
 * 一覧表のプロパティ
 */
interface Props {
  loading: boolean;
  errorCode: number;
  devMode: string; // 0:初期表示 1:確認済みモード 2:Bispot更新モード
  data: DataType[];
}

/**
 * ソート処理
 * @param a
 * @param b
 */
const doCompareStr = (a: string, b: string) => {
  if (a === null || a === undefined) {
    return -1;
  }
  if (b === null || b === undefined) {
    return 1;
  }
  return a.localeCompare(b);
};

/**
 * 対象期間ステータス
 * @param a
 */
const timeSlotSortNum = (a: TimeSlot) => {
  switch (a) {
    case TimeSlot.morning:
      return 1;
    case TimeSlot.afternoon:
      return 2;
    case TimeSlot.night:
      return 3;
    case TimeSlot.all_day:
      return 4;
    default:
      return 5;
  }
};

/**
 * 一覧表
 * @param data
 * @param loading
 * @param errorCode
 * @constructor
 */
export const TableLayout: React.FC<Props> = ({ data, loading, errorCode }) => {
  // 画面遷移用
  const navigator = useNavigate();
  const [openConflictModal, setOpenConflictModal] = useState(false);
  const { getConflict } = useTaskListStore((selector) => selector);
  const [conflictData, setConflictData] = useState<WorkListReponse>(null);
  const [detailApi, detailContext] = useWorkDetails();
  const [today, setToday] = useState<dayjs.Dayjs>(dayjs());
  const indicator = useIndicator();

  const onConflictStatusClick = (conflictId: number, planId: number) => {
    indicator.show();
    getConflict(conflictId, planId).then((resp) => {
      setConflictData(resp);
      setOpenConflictModal(true);
      setToday(dayjs(resp[0].plan.start));
    }).catch((e) => {
      if (e instanceof ApiError) {
        const { status } = e.response;
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          window.alert(Messages.ERR_DIALOG_RETURN_LOGIN);
          navigator('/login', { state: { message: Messages.ERR_401 } });
        } else if (status === -1) {
          window.alert(Messages.ERR_DIALOG_TIMEOUT);
        } else {
          window.alert(Messages.ERR_DIALOG_RETRY_OPERATION);
        }
      }
    }).finally(() => {
      indicator.hide();
    });
  };

  // @ts-ignore
  const columns: TableColumnsType<DataType> = useMemo(() => {
    return [
      {
        title: '',
        dataIndex: 'edit',
        width: 120,
        fixed: 'left',
        render: (_, data) => (
          <Button
            onClick={() => {
              detailApi.show(data.id, data.planId);
            }}
          >
            詳細
          </Button>
        ),
      },
      {
        title: '競合',
        dataIndex: 'conflict',
        fixed: 'left',
        sorter: (a, b) => {
          return a.conflict - b.conflict;
        },
        render: (conflict: ConflictStatus, data: DataType) => {
          if (conflict !== ConflictStatus.CONFLICT) {
            // 競合あり
            return (
              <div>
                <Button block onClick={() => { onConflictStatusClick(data.conflictId, data.planId); }}>
                  <CheckOutlined style={{ color: '#0e0' }}/>
                </Button>
              </div>
            );
          }
          // 競合なし
          return (
            <div>
              <Button block onClick={() => { onConflictStatusClick(data.conflictId, data.planId); }}>
                <span style={{ color: '#f00', fontSize: '1.2em' }}>※</span>
              </Button>
            </div>
          );
        },
      },
      {
        title: '街区',
        dataIndex: 'division',
        width: 80,
        sorter: (a, b) => doCompareStr(a.division, b.division),
      },
      {
        title: '階',
        dataIndex: 'floor',
        sorter: (a, b) => doCompareStr(a.floor, b.floor),
      },
      {
        title: '区画',
        dataIndex: 'block',
        sorter: (a, b) => doCompareStr(a.block, b.block),
      },
      {
        title: '作業場所',
        dataIndex: 'target',
        sorter: (a, b) => doCompareStr(a.target, b.target),
      },
      {
        title: '作業区分',
        dataIndex: 'sections',
        sorter: (a, b) => {
          const aSec = a.sections?.join('-') ?? '';
          const bSec = b.sections?.join('-') ?? '';
          return aSec.localeCompare(bSec);
        },
        render: (sections: SectionDisplayKeys[]) => {
          return sections.map((section) => {
            return <SectionIcon key={section} sectionCode={section} />;
          });
        },
      },
      {
        title: '作業内容',
        dataIndex: 'content',
        sorter: (a, b) => doCompareStr(a.content, b.content),
      },
      {
        title: '作業件名',
        dataIndex: 'title',
        sorter: (a, b) => doCompareStr(a.title, b.title),
      },
      {
        title: '作業開始日',
        dataIndex: 'start_day',
        sorter: (a, b) => doCompareStr(a.start_day, b.start_day),
        render: (start_day: string) => {
          return dayjs(start_day, 'YYYY-MM-DD').format('YYYY/MM/DD');
        },
      },
      {
        title: '作業終了日',
        dataIndex: 'end_day',
        sorter: (a, b) => doCompareStr(a.end_day, b.end_day),
        render: (end_day: string) => {
          return dayjs(end_day, 'YYYY-MM-DD').format('YYYY/MM/DD');
        },
      },
      {
        title: '作業時間',
        dataIndex: 'work_time',
        sorter: (a, b) => doCompareStr(a.work_time, b.work_time),
      },
      {
        title: '時間帯',
        dataIndex: 'span_of_time',
        sorter: (a, b) => {
          if (a.span_of_time === null || a.span_of_time === undefined) {
            return -1;
          }
          if (b.span_of_time === null || b.span_of_time === undefined) {
            return 1;
          }
          return timeSlotSortNum(a.span_of_time) - timeSlotSortNum(b.span_of_time);
        },
        render: (span_of_time: TimeSlot) => {
          return TimeSlotList.find((timeSlot) => timeSlot.key === span_of_time)?.value ?? '不明';
        },
      },
      {
        title: '作業会社名',
        dataIndex: 'companyName',
        sorter: (a, b) => doCompareStr(a.companyName, b.companyName),
      },
      {
        title: '申請日',
        dataIndex: 'applyDate',
        render: (applyDate: string) => {
          return dayjs(applyDate, 'YYYY-MM-DD').format('YYYY/MM/DD');
        },
        sorter: (a, b) => doCompareStr(a.applyDate, b.applyDate),
      },
      {
        title: '総合管理受付日時',
        dataIndex: 'createdAt',
        sorter: (a, b) => doCompareStr(a.createdAt, b.createdAt),
        render: (createdAt: string) => {
          return dayjs(createdAt).format('YYYY/MM/DD HH:mm:ss');
        },
      },
    ];
  }, []);

  const errorText = useMemo(() => {
    if (errorCode === 0) {
      return undefined;
    }
    return <Alert message={Messages.ERR_OTHER} type="error" />;
  }, [errorCode]);

  return (
    <div>
      {detailContext}
      <div className="section_no_backcolor">
        <Table<DataType>
            columns={columns}
            pagination={false}
            loading={loading}
            locale={{ emptyText: errorText }}
            showHeader={data.length > 0}
            dataSource={data} />
      </div>
      <Modal
        title="競合確認"
        centered
        open={openConflictModal}
        width={1600}
        closable={true}
        cancelText={'閉じる'}
        onOk={() => setOpenConflictModal(false)}
        onCancel={() => setOpenConflictModal(false)}
        footer={[
          <Button key={'0'} onClick={() => setOpenConflictModal(false)}>閉じる</Button>,
        ]}
      >
        <div>
          <ConflictListPage scheduleList={conflictData} status={ScheduleStatus.show} viewDate={today}/>
        </div>
      </Modal>
    </div>
  );
};
